import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Ban, Loader2, PenLine, Save, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  COMPANY,
  employmentLetter,
  fillLetter,
  placeholdersIn,
  type EngagementKind,
} from '@jt/domain';
import { api } from '../lib/api';
import { DocumentEditor } from '../components/DocumentEditor';
import { PageNav } from '../components/PageNav';
import { PrintContractButton, PrintableContract } from '../components/PrintContract';
import { SigningFields, SigningSequence } from '../components/SigningSequence';
import {
  GAP_ATTRIBUTE,
  InlineField,
  SigningBar,
  inlineFilling,
  type InlineFieldSpec,
} from '../components/InlineFields';
import { useToast } from '../components/Toast';
import { useSession } from '../lib/session';
import type { SigningParty } from '../lib/types';

/**
 * One contract, and whichever half of it you are allowed to fill.
 *
 * ## The one rule this screen exists to make obvious
 *
 * The company signs, then the employee. Enforced in the domain and refused by
 * the API, so nothing here is load-bearing — but a rule the software enforces
 * and the screen does not explain reads as a bug. So the order is drawn at the
 * top, the boxes belonging to the other side are shown filled and unwritable
 * rather than hidden, and the button says whose signature it is collecting.
 *
 * ## Why saving and signing are separate buttons
 *
 * Somebody filling in their KRA PIN from a photograph of a certificate is
 * going to lose the tab, and a form that only commits on signature loses their
 * work. Save keeps the draft; signing is the deliberate second act. Signing
 * also posts whatever is in the boxes, so the last keystroke cannot be
 * stranded between the two.
 */
export function StaffContractPage() {
  const { id = '' } = useParams();
  const { user, can } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();

  const contract = useQuery({
    queryKey: ['staff-contract', id],
    queryFn: () => api.staffContract(id),
    enabled: Boolean(id),
  });

  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [typedName, setTypedName] = useState('');
  const [signatoryTitle, setSignatoryTitle] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');

  /*
   * Fill the boxes from the server once per contract, and then leave them
   * alone.
   *
   * Two failure modes to avoid, and they pull in opposite directions. Watching
   * the query object means a background refetch wipes whatever somebody is
   * part-way through typing. Watching only the id means the effect fires while
   * the query is still loading, finds nothing, and never runs again — the form
   * renders permanently empty over values that did arrive.
   *
   * So the id is remembered in a ref and the effect claims it exactly once, on
   * the first render where the data is actually there.
   */
  const hydrated = useRef<string | null>(null);
  useEffect(() => {
    if (!contract.data || hydrated.current === id) return;
    hydrated.current = id;
    setDraft(contract.data.values ?? {});
  }, [id, contract.data]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['staff-contract', id] });
    await queryClient.invalidateQueries({ queryKey: ['staff-contracts'] });
  };

  const it = contract.data;
  const mine = it ? it.holder.id === user?.id : false;
  const canManage = can('staff_contract:manage');

  /*
   * Which side you are filling in.
   *
   * Somebody can be both — an administrator with a contract of their own — and
   * then the side that matters is the one whose turn it is. When it is nobody's
   * turn the value is only used to decide which boxes to grey out, so either
   * answer is harmless.
   */
  const acting: SigningParty | null = !it
    ? null
    : it.turn === 'company' && canManage
      ? 'company'
      : it.turn === 'counterparty' && mine
        ? 'counterparty'
        : null;

  /*
   * Saving the letter's wording. The company's route only — see the editor's
   * own note for why the employee's does not take it.
   */
  const saveLetter = useMutation({
    mutationFn: (body: string) => api.saveStaffContractCompanyFields(id, draft, { body }),
    onSuccess: async () => {
      toast.done('Saved. Nothing is signed yet.');
      await invalidate();
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  const save = useMutation({
    mutationFn: () => {
      const party = acting ?? (mine ? 'counterparty' : 'company');
      return party === 'company'
        ? api.saveStaffContractCompanyFields(id, draft)
        : api.saveStaffContractFields(id, draft);
    },
    onSuccess: async () => {
      toast.done('Saved. Nothing is signed yet.');
      await invalidate();
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  const sign = useMutation({
    mutationFn: () =>
      acting === 'company'
        ? api.signStaffContractForCompany(id, {
            typedName,
            title: signatoryTitle || undefined,
            fields: draft,
          })
        : api.signStaffContract(id, { typedName, fields: draft }),
    onSuccess: async (result) => {
      toast.done(
        result.complete
          ? 'Signed by both sides. It is in force.'
          : `Signed. It is now with ${result.next?.label ?? 'the other side'}.`,
      );
      setTypedName('');
      await invalidate();
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  const remind = useMutation({
    mutationFn: () => api.remindStaffContract(id),
    onSuccess: (result) =>
      toast.done(
        result.to
          ? `Sent to ${result.to}. They have a link straight to this page.`
          : 'Sent.',
      ),
    onError: (error: Error) => toast.failed(error.message),
  });

  const cancel = useMutation({
    mutationFn: () => api.voidStaffContract(id, reason),
    onSuccess: async () => {
      toast.done('Cancelled. Nobody can sign it now.');
      setVoiding(false);
      setReason('');
      await invalidate();
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  if (contract.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  if (contract.isError || !it) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <h2 className="font-heading text-xl">No such contract</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          It may have been cancelled, or it may belong to somebody else.
        </p>
      </div>
    );
  }

  const signed = it.signatures.map((one) => one.party);
  const outstanding = acting ? it.outstanding[acting] : null;

  const closed = it.status === 'VOIDED' || it.status === 'SUPERSEDED';

  /*
   * ── Filling the letter in, in the letter ────────────────────────────────
   *
   * Both sides, from one piece of state, because `acting` already says which
   * side the reader is on and the fields already say which side each belongs
   * to. An employee sees the gaps in the clauses that concern them; whoever
   * signs for the company sees theirs; neither is shown a grid of boxes
   * underneath a letter that never says which sentence they belong to.
   */
  const SIGNATURE_KEY = '__typedName';
  const fillingNow = Boolean(acting) && !closed;

  const myGaps: InlineFieldSpec[] = it.fields
    .filter((field) => field.party === acting)
    .map((field) => ({
      key: field.key,
      label: field.label,
      kind: field.kind,
      required: field.required,
      hint: field.hint,
      options: field.options,
    }));

  /*
   * Split by what the wording actually contains.
   *
   * Most fields are gaps in the text and are typed into the text. A few have
   * nowhere to go: "Installation fee" is a choice whose *result* is the
   * sentence `{{feeDescription}}`, so there is no `{{feeBasis}}` anywhere to
   * put a dropdown in. Six of this agreement's fields are like that, and the
   * first version of this screen dropped the form outright — which left no way
   * at all to set the fee, the term, or the exclusivity grant.
   *
   * Asked of the body rather than listed here, because the body is editable:
   * a clause rewritten to mention a field directly gains an inline gap for it
   * with nothing to change on this page.
   */
  const gaps = placeholdersIn(it.body);
  const inDocument = myGaps.filter((field) => gaps.has(field.key));
  const offDocument = it.fields.filter(
    (field) => field.party === acting && !gaps.has(field.key),
  );

  const barFields: InlineFieldSpec[] = fillingNow
    ? [...myGaps, { key: SIGNATURE_KEY, label: 'Your signature', kind: 'signature', required: true }]
    : [];

  const filling = inlineFilling(barFields, it.values, { ...draft, [SIGNATURE_KEY]: typedName });

  const slots = fillingNow
    ? Object.fromEntries(
        inDocument.map((field) => [
          field.key,
          <InlineField
            key={field.key}
            field={field}
            value={filling.valueOf(field.key)}
            onChange={(value) => setDraft((old) => ({ ...old, [field.key]: value }))}
          />,
        ]),
      )
    : undefined;

  const leaveOpen = fillingNow
    ? new Set<string>(inDocument.map((field) => field.key))
    : undefined;

  const date = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      : null;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageNav
        crumbs={[
          { label: 'Contracts', to: '/staff-contracts' },
          { label: it.reference },
        ]}
      />

      <div className="flex flex-wrap items-start justify-between gap-3 print:hidden">
        <div>
          <h1 className="font-heading text-2xl">{it.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {it.reference} · {it.holder.name} · {it.engagement.label}, {it.engagement.months}{' '}
            months
            {date(it.startDate) ? ` from ${date(it.startDate)}` : ''}
            {date(it.endDate) ? ` to ${date(it.endDate)}` : ''}
          </p>
        </div>
        <PrintContractButton />
      </div>

      {closed && (
        <p className="card border-accent-300 bg-accent-50 text-sm text-accent-900">
          This contract was cancelled{it.voidReason ? ` — ${it.voidReason}` : ''}. It cannot be
          signed.
        </p>
      )}

      {/* ── Who signs, and in what order ──────────────────────────────── */}
      <section className="card space-y-3 print:hidden">
        <h2 className="font-heading text-lg">Signing order</h2>
        <SigningSequence
          signed={signed}
          turn={closed ? null : it.turn}
          counterparty="employee"
          signatures={it.signatures}
        />

        {/*
          The handover. Named explicitly, because "awaiting employee" leaves
          the reader working out whether that means them.
        */}
        {!closed && it.next && (
          <p className="flex items-center gap-2 rounded-xl bg-muted/40 p-3 text-sm">
            <ArrowRight className="h-4 w-4 shrink-0 text-primary-600" aria-hidden="true" />
            <span>
              Now with <strong>{it.next.label}</strong>
              {acting ? ' — that is you.' : '. You will be told when it is your turn.'}
            </span>
          </p>
        )}
      </section>

      {/*
        ── The letter, read and edited in the same place ─────────────────

        The same component the service agreement and the building agreement
        use. Values come from the draft rather than the saved row, so somebody
        typing a salary watches it appear in the sentence that binds them, and
        anything unanswered shows as a rule to write on.

        Only the company may rewrite it, and only while it is their turn. An
        employee filling in a KRA PIN has no business editing the offer they
        are being made — the server refuses it on their route regardless of
        what this page shows.
      */}
      <DocumentEditor
        heading="The letter"
        blurb="The offer as it will be read and signed. The gaps fill in as they are typed."
        standard={employmentLetter(it.kind as EngagementKind)}
        body={it.body}
        values={{ ...it.values, ...draft }}
        canEdit={acting === 'company' && !closed}
        saving={saveLetter.isPending}
        onSave={(body) => saveLetter.mutateAsync(body)}
        slots={slots}
        leaveOpen={leaveOpen}
        note="Nothing has been signed. Once you sign for the company this is what the employee is asked to agree to."
      />

      {/* ── The other side's answers, read-only ───────────────────────── */}
      <FilledByTheOtherSide fields={it.fields} values={it.values} acting={acting} />

      {/* ── Your half ─────────────────────────────────────────────────── */}
      {acting && !closed && (
        <section className="card space-y-4 print:hidden">
          <div>
            <h2 className="font-heading text-lg">
              {acting === 'company' ? 'The terms, and the company’s signature' : 'Your details'}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Type into the highlighted gaps in the letter above — every one of them has to be
              filled before it can be signed.
            </p>
          </div>

          {/* Whatever the letter has no blank for — see the note above. */}
          {acting && offDocument.length > 0 && (
            <SigningFields
              fields={offDocument}
              values={{ ...it.values, ...draft }}
              party={acting}
              onChange={(key, value) => setDraft((old) => ({ ...old, [key]: value }))}
            />
          )}

          {outstanding && (
            <p className="rounded-xl bg-accent-50 p-3 text-sm text-accent-900">{outstanding}</p>
          )}

          <div className="flex flex-wrap items-end gap-3 border-t border-border pt-4">
            <label className="min-w-[16rem] flex-1 text-sm">
              Type your full name to sign
              <input
                {...{ [GAP_ATTRIBUTE]: SIGNATURE_KEY }}
                className="input signature-ink mt-1 w-full text-2xl"
                value={typedName}
                onChange={(event) => setTypedName(event.target.value)}
                placeholder={
                  acting === 'company'
                    ? String(it.values.companySignatoryName ?? 'Signing for the company')
                    : it.holder.name
                }
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                It has to match the name on the contract. Typing it is your signature, and the
                time, the address you signed from and this exact wording are kept with it.
              </span>
            </label>

            {acting === 'company' && (
              <label className="min-w-[12rem] text-sm">
                Your title
                <input
                  className="input mt-1 w-full"
                  value={signatoryTitle}
                  onChange={(event) => setSignatoryTitle(event.target.value)}
                  placeholder="Chief Executive Officer"
                />
              </label>
            )}
          </div>

          {/*
            * Saving stays here; signing moved to the bar at the bottom of the
            * window, next to the count of what is still outstanding. Somebody
            * who has read to the end of an employment letter should not have
            * to scroll back to find out whether they may sign it.
            */}
          <button
            type="button"
            className="btn-secondary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            Save and finish later
          </button>
        </section>
      )}

      {/*
        * What is left, and the pen.
        *
        * Sticky to the bottom of the window, because an employment letter runs
        * to several screens and the answer to "can I sign this yet?" must not
        * depend on where somebody happens to have scrolled to.
        */}
      {fillingNow && (
        <SigningBar
          outstanding={filling.outstanding}
          total={barFields.length}
          onJump={filling.jumpTo}
        >
          <button
            type="button"
            className="btn-primary"
            disabled={typedName.trim().length < 3 || sign.isPending}
            onClick={() => sign.mutate()}
          >
            {sign.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <PenLine className="h-4 w-4" aria-hidden="true" />
            )}
            {acting === 'company' ? 'Sign for the company' : 'Sign the contract'}
          </button>
        </SigningBar>
      )}

      {/*
        ── Nudging the person it is waiting on ─────────────────────────

        Only while it is actually their turn. The email goes out on its own
        when the company signs; this is for the contracts raised before that
        existed, and for "they say they never got it".
      */}
      {canManage && it.status === 'AWAITING_EMPLOYEE' && (
        <section className="print:hidden">
          <button
            type="button"
            className="btn-secondary"
            disabled={remind.isPending}
            onClick={() => remind.mutate()}
          >
            {remind.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Send className="h-4 w-4" aria-hidden="true" />
            )}
            Email them that it is waiting
          </button>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Goes to the address on their account, with a link to this page. Sent automatically
            when the company signs — this is for sending it again.
          </p>
        </section>
      )}

      {/* ── Cancelling one nobody has finished ────────────────────────── */}
      {canManage && !closed && it.status !== 'SIGNED' && (
        <section className="print:hidden">
          {voiding ? (
            <div className="card space-y-3">
              <label className="block text-sm">
                Why is it being cancelled?
                <input
                  className="input mt-1 w-full"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Salary corrected — a replacement will be raised"
                />
                <span className="mt-1 block text-xs text-muted-foreground">
                  Kept with the contract. Whoever asks about it later will read this.
                </span>
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-danger"
                  disabled={reason.trim().length < 3 || cancel.isPending}
                  onClick={() => cancel.mutate()}
                >
                  {cancel.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  Cancel this contract
                </button>
                <button type="button" className="btn-secondary" onClick={() => setVoiding(false)}>
                  Leave it
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-accent-700 hover:underline"
              onClick={() => setVoiding(true)}
            >
              <Ban className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
              Cancel this contract
            </button>
          )}
        </section>
      )}

      {/*
        The copy that comes out of a printer, and the one the browser's
        "Save as PDF" produces. Hidden on screen: the same markup, without the
        scroll box that would print as a cropped box.
      */}
      <PrintableContract
        title={it.title}
        reference={it.reference}
        company="GoSpeedNet"
        body={fillLetter(it.body, it.values)}
        issuedOn={it.companySignedAt}
        standing={it.status === 'SIGNED' ? 'Executed by both parties' : null}
        parties={[
          { role: 'The employer', name: COMPANY.legalName },
          {
            role: 'The employee',
            name: it.holder.name,
            lines: [it.engagement.label, it.holder.email],
          },
        ]}
        counterpartyLabel="The employee"
        signatures={it.signatures.map((one) => ({
          party: one.party,
          name: one.signerName,
          title: one.signerTitle,
          signedAt: one.signedAt,
        }))}
      />
    </div>
  );
}

/**
 * What the other party filled in, shown and not editable.
 *
 * Hiding it would be easier and worse: an employee is entitled to read the
 * salary they are being offered before they sign for it, and HR needs to see
 * the ID number that came back. What they must not be able to do is type in
 * each other's boxes, which is a different thing from seeing them — and which
 * the API refuses regardless, dropping any key that does not belong to the
 * party sending it.
 */
function FilledByTheOtherSide({
  fields,
  values,
  acting,
}: {
  fields: Array<{ key: string; label: string; party: SigningParty; kind: string }>;
  values: Record<string, unknown>;
  acting: SigningParty | null;
}) {
  // With no turn of your own, everything is somebody else's — show it all.
  const theirs = fields.filter((field) => (acting ? field.party !== acting : true));
  const filled = theirs.filter((field) => {
    const value = values[field.key];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });

  if (filled.length === 0) return null;

  return (
    <section className="card print:hidden">
      <h2 className="font-heading text-lg">
        {acting ? 'Filled in by the other side' : 'What is on the contract'}
      </h2>
      <dl className="mt-2 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        {filled.map((field) => (
          <div key={field.key} className="flex flex-wrap items-baseline justify-between gap-2">
            <dt className="text-sm text-muted-foreground">{field.label}</dt>
            <dd
              className={`text-sm font-medium ${
                field.kind === 'signature' || field.kind === 'initials'
                  ? 'signature-ink text-lg'
                  : ''
              }`}
            >
              {String(values[field.key])}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
